<script setup lang="ts">
import type { ChartPoint } from '@/lib/charts/types'
import { VisAxis, VisGroupedBar, VisGroupedBarSelectors, VisTooltip, VisXYContainer } from '@unovis/vue'
import { useElementSize } from '@vueuse/core'
import { format } from 'date-fns'
import { computed, useTemplateRef } from 'vue'

const props = withDefaults(defineProps<{
  points: ChartPoint[]
  color?: string
  height?: number
  /** Tick/tooltip label granularity. 'day' shows 'd MMM', 'month' shows 'MMM yy'. */
  granularity?: 'day' | 'month'
  valueFormat?: (value: number) => string
}>(), { height: 220, color: 'var(--ui-primary)', granularity: 'day' })

interface Row { x: number, y: number }

const wrapper = useTemplateRef<HTMLElement | null>('wrapper')
const { width } = useElementSize(wrapper)
const chartWidth = computed(() => Math.max(width.value, 320))

const rows = computed<Row[]>(() => props.points.map(point => ({ x: point.x, y: point.y ?? 0 })))

const x = (row: Row) => row.x
const y = (row: Row) => row.y
const barColor = () => props.color

// Bars are evenly spaced buckets (days or months); telling GroupedBar the exact
// step keeps bar width correct even though calendar months vary in length.
const dataStep = computed(() => {
  if (rows.value.length < 2)
    return undefined
  return rows.value[1].x - rows.value[0].x
})

const formatValue = computed(() =>
  props.valueFormat ?? ((value: number) => new Intl.NumberFormat('en', { maximumFractionDigits: 2 }).format(value)),
)

function formatTick(value: number): string {
  const date = new Date(value)
  return props.granularity === 'month' ? format(date, 'MMM yy') : format(date, 'd MMM')
}

function tooltip(row: Row): string {
  return `<div class="text-dimmed mb-1 text-xs">${formatTick(row.x)}</div><div class="font-medium">${formatValue.value(row.y)}</div>`
}
</script>

<template>
  <div ref="wrapper" class="bar-chart w-full">
    <VisXYContainer :data="rows" :height="height" :width="chartWidth" :padding="{ top: 12, bottom: 24, left: 8, right: 8 }">
      <VisGroupedBar :x="x" :y="[y]" :color="barColor" :data-step="dataStep" :rounded-corners="4" :bar-min-height="2" />
      <VisAxis type="x" :x="x" :tick-format="formatTick" :grid-line="false" :num-ticks="6" />
      <VisTooltip :triggers="{ [VisGroupedBarSelectors.bar]: (d: Row) => tooltip(d) }" />
    </VisXYContainer>
  </div>
</template>

<style scoped>
.bar-chart {
  --vis-axis-tick-color: transparent;
  --vis-axis-tick-label-color: var(--ui-text-dimmed);
  --vis-tooltip-background-color: var(--ui-bg);
  --vis-tooltip-border-color: var(--ui-border);
  --vis-tooltip-text-color: var(--ui-text-highlighted);
  --vis-tooltip-padding: 0.5rem 0.75rem;
}
</style>
